import { useEffect, useState } from "react"
import { getAllVeterinaries, updateVeterinary } from "../../services/VeterinaryServices"
import { useAuth } from "../../contexts/authContext"
import { Building2, CheckCircle, XCircle, Search, MapPin, Phone, Calendar } from "lucide-react"
import "../../assets/styles/Veterinaries.css"
import { formatDate } from "../../utils/dates/formatDate"

const Veterinaries = () => {
  const { getToken } = useAuth()
  const [veterinaries, setVeterinaries] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [updatingVetId, setUpdatingVetId] = useState(null)

  useEffect(() => {
    const fetchVeterinaries = async () => {
      try {
        const token = await getToken()
        const data = await getAllVeterinaries(token)
        setVeterinaries(data)
      } catch (error) {
        console.error("Error al obtener las veterinarias:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchVeterinaries()
  }, [getToken])

  const handleToggleActive = async (vetId, currentStatus) => {
    try {
      setUpdatingVetId(vetId)
      const token = await getToken()
      await updateVeterinary(token, vetId, { active: !currentStatus })
      setVeterinaries(veterinaries.map((vet) => (vet.id === vetId ? { ...vet, active: !currentStatus } : vet)))
    } catch (error) {
      console.error("Error al cambiar el estado de la veterinaria:", error)
    } finally {
      setUpdatingVetId(null)
    }
  }

  const filteredVeterinaries = veterinaries.filter(
    (vet) =>
      vet.legalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vet.address.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  return (
    <div className="veterinaries-container">
      <div className="veterinaries-header">
        <h1 className="veterinaries-title">
          <Building2 className="veterinaries-icon" />
          Gestión de Veterinarias
        </h1>
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Buscar veterinarias..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="veterinaries-stats">
        <div className="stat-card">
          <CheckCircle className="stat-icon active" />
          <div className="stat-info">
            <span className="stat-value">{veterinaries.filter((vet) => vet.active).length}</span>
            <span className="stat-label">Veterinarias Activas</span>
          </div>
        </div>
        <div className="stat-card">
          <XCircle className="stat-icon inactive" />
          <div className="stat-info">
            <span className="stat-value">{veterinaries.filter((vet) => !vet.active).length}</span>
            <span className="stat-label">Veterinarias Inactivas</span>
          </div>
        </div>
        <div className="stat-card">
          <Building2 className="stat-icon total" />
          <div className="stat-info">
            <span className="stat-value">{veterinaries.length}</span>
            <span className="stat-label">Total Veterinarias</span>
          </div>
        </div>
      </div>

      <div className="veterinaries-table-container">
        {loading ? (
          <table className="veterinaries-table">
            <thead>
              <tr>
                {/* <th className="vet-col-logo"></th> */}
                <th className="vet-col-name">Veterinaria</th>
                <th className="vet-col-contact">Contacto</th>
                <th className="vet-col-date">Fecha de registro</th>
                <th className="vet-col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, index) => (
                <tr key={index} className="vet-row skeleton-row">
                  {/* <td className="vet-col-logo">
                    <div className="skeleton vet-logo-skeleton"></div>
                  </td> */}
                  <td className="vet-col-name">
                    <div className="skeleton skeleton-text-lg"></div>
                    <div className="skeleton skeleton-text-sm"></div>
                  </td>
                  <td className="vet-col-contact">
                    <div className="skeleton skeleton-text-sm"></div>
                    <div className="skeleton skeleton-text-sm"></div>
                  </td>
                  <td className="vet-col-date">
                    <div className="skeleton skeleton-text-sm"></div>
                  </td>
                  <td className="vet-col-actions">
                    <div className="skeleton skeleton-button"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="veterinaries-table">
            <thead>
              <tr>
                {/* <th className="vet-col-logo"></th> */}
                <th className="vet-col-name">Veterinaria</th>
                <th className="vet-col-contact">Contacto</th>
                <th className="vet-col-date">Fecha de registro</th>
                <th className="vet-col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredVeterinaries.length === 0 ? (
                <tr>
                  <td colSpan="4">
                    <div className="no-results">
                      <p>No se encontraron veterinarias que coincidan con la búsqueda.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredVeterinaries.map((vet) => (
                  <tr key={vet.id} className="vet-row">
                    {/* <td className="vet-col-logo">
                      <div className="vet-logo-container">
                        <img
                          src={vet.logo || "/placeholder.svg?height=100&width=100"}
                          alt={vet.legalName}
                          className="vet-logo"
                          onError={(e) => {
                            e.target.src = "/placeholder.svg?height=100&width=100"
                          }}
                        />
                        <span className={`status-indicator ${vet.active ? "active" : "inactive"}`}></span>
                      </div>
                    </td> */}
                    <td className="vet-col-name">
                      <div className="vet-name">
                        {vet.legalName}
                        <span className={`status-indicator ${vet.active ? "active" : "inactive"}`}></span>
                      </div>
                      <div className="vet-address">
                        <MapPin className="info-icon" />
                        {vet.address}
                      </div>
                    </td>
                    <td className="vet-col-contact">
                      <div className="vet-phone">
                        <Phone className="info-icon" />
                        {vet.phoneNumber}
                      </div>
                    </td>
                    <td className="vet-col-date">
                      <div className="vet-date">
                        <Calendar className="info-icon" />
                        {formatDate(vet.registrationDate)}
                      </div>
                    </td>
                    <td className="vet-col-actions">
                      <button
                        className={`btn ${vet.active ? "btn-destructive" : "btn-primary"}`}
                        onClick={() => handleToggleActive(vet.id, vet.active)}
                        disabled={updatingVetId === vet.id}
                      >
                        {updatingVetId === vet.id ? (
                          <span className="loading-spinner"></span>
                        ) : vet.active ? (
                          <>
                            <XCircle className="btn-icon" />
                            <span className="btn-text">Desactivar</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="btn-icon" />
                            <span className="btn-text">Activar</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default Veterinaries
